'use client';

import { useEffect, useRef, useState } from 'react';
import { currentProgramBrowserRead } from '@/lib/experience/current-program-browser-read-actions';
import type { ProgramNavigationHint } from '@/lib/experience/program-navigation-hint';

export type ProgramHintBinding = {
  actorId: string;
  tenantId: string;
  clientId: string;
  contextRevision: string;
  asOfDate: string;
  pathname: string;
};

/** Render-time key check clears old data before effects run; cleanup + generation
 * reject late completion, including identity A -> B -> A and a day rollover. */
export function useProgramNavigationHint(binding: ProgramHintBinding | null) {
  const key = binding ? JSON.stringify(binding) : null;
  const latest = useRef(key);
  latest.current = key;
  const generation = useRef(0);
  const [state, setState] = useState<{ key: string; hint: ProgramNavigationHint } | null>(null);
  useEffect(() => {
    const attempt = ++generation.current;
    setState(null);
    if (!binding || !key) return;
    let live = true;
    void currentProgramBrowserRead('programNavigationHint', {
      clientId: binding.clientId,
      pathname: binding.pathname,
      expectedContextRevision: binding.contextRevision,
      expectedAsOfDate: binding.asOfDate,
      expectedActorId: binding.actorId,
      expectedTenantId: binding.tenantId,
    })
      .then((result) => {
        if (!live || attempt !== generation.current || latest.current !== key || !result.success)
          return;
        const hint = result.data;
        if (
          hint.kind === 'unavailable' ||
          hint.asOfDate !== binding.asOfDate ||
          !binding.contextRevision.startsWith(`${hint.authorityRevision}:`)
        )
          return;
        setState({ key, hint });
      })
      .catch(() => {
        /* Optional assistance never replaces an access/read failure. */
      });
    return () => {
      live = false;
      generation.current++;
    };
    // Every binding dimension is represented by the immutable key.
  }, [key]);
  return key && state?.key === key ? state.hint : null;
}
